import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Snackbar,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { addCompetition } from '../../store/competitionSlice';
import { FileType, validateFile, resolveFileType, processCSVFile, processJSONFile } from '../../utils/fileParser';
import PTFParser from '../../utils/ptfParser.ts';
import JSONParser from '../../utils/jsonParser.ts';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const FileUpload = React.forwardRef(({
  onFileSelect,
  onImport = () => { },
  acceptedFormats,
  existingCompetitions = [],
  onClose = () => { },
  onDuplicateFound = () => { },
  showMergePrompt = false,
  currentCompetition = null
}, ref) => {
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFile = async (file) => {
    const validation = await validateFile(file);
    resetParseStates();

    setValidation(validation);
    if (validation.isValid) {
      // First check if this is a competition file type
      if ([FileType.PTF, FileType.JSON.COMPETITION].includes(validation.type)) {
        // Read the file to check for existing competition
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            let parser;

            if (validation.type === FileType.PTF) {
              parser = await PTFParser.fromFile(file);
            } else {
              // parser = new JSONParser(e.target.result);
              parser = await JSONParser.fromFile(file);
            }

            // Check if competition exists
            const existingCompetition = parser.competitionExists(existingCompetitions);
            if (existingCompetition) {
              setSelectedFile({
                name: file.name,
                size: (file.size / 1024).toFixed(2) + ' KB',
                type: validation.type,
                lastModified: new Date(file.lastModified).toLocaleDateString(),
                file: file,
                parsedData: parser.parse(),
                existingCompetition
              });
              onDuplicateFound({
                name: file.name,
                size: (file.size / 1024).toFixed(2) + ' KB',
                type: validation.type,
                lastModified: new Date(file.lastModified).toLocaleDateString(),
                file: file,
                parsedData: parser.parse(),
                existingCompetition
              });
              return;
            }

            // If we get here, it's a new competition
            setError(null);
            setSuccess(true);
            setSelectedFile({
              name: file.name,
              size: (file.size / 1024).toFixed(2) + ' KB',
              type: validation.type,
              lastModified: new Date(file.lastModified).toLocaleDateString(),
              file: file
            });
            onFileSelect(file);

            // Automatically process the file
            if (validation.type === FileType.PTF) {
              processFilePTF(file);
            } else if (validation.type === FileType.JSON.COMPETITION) {
              processFileJSON(file, validation.type);
            }
          } catch (err) {
            setError(err.message);
            setSuccess(false);
            setSelectedFile(null);
          }
        };
        reader.readAsText(file);
      } else {
        // For non-competition files (like CSV), proceed as normal
        setError(null);
        setSuccess(true);
        setSelectedFile({
          name: file.name,
          size: (file.size / 1024).toFixed(2) + ' KB',
          type: validation.type,
          lastModified: new Date(file.lastModified).toLocaleDateString(),
          file: file
        });
        onFileSelect(file);

        // Process CSV files
        if (validation.type.startsWith('CSV.')) {
          processFileCSV(file, validation.type);
        }

        // Process JSON files
        if (validation.type.startsWith('JSON.')) {
          processFileJSON(file, validation.type);
        }

      }
    } else {
      setError(validation.error);
      setSuccess(false);
      setSelectedFile(null);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length) {
      await handleFile(files[0]);
    }
  };

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validation, setValidation] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parseProgress, setParseProgress] = useState(0);
  const [parseResults, setParseResults] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');


  const resetParseStates = () => {
    setParseProgress(0);
    setParseResults(null);
    setParsing(false);
    setParseError(null);
  };

  const resetAll = () => {
    setSelectedFile(null);
    setSuccess(false);
    setValidation(null);
    setError(null);
    setParseError(null);
    resetParseStates();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  React.useImperativeHandle(ref, () => ({
    reset: resetAll
  }));

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files.length) {
      await handleFile(files[0]);
    }
  };

  const processFileJSON = async (file, type) => {
    setParsing(true);
    setParseProgress(0);
    setParseError(null);

    if (type !== FileType.JSON.COMPETITION) {
      try {
        const data = await processJSONFile(file, type, currentCompetition);

        // Simulate processing time with a pause at 100%
        let progress = 0;
        const interval = setInterval(() => {
          progress += 5;
          setParseProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            // Pause for 1 second at 100% before setting results
            setTimeout(() => {
              setParseResults(data);
              setSuccess(true);
              setParsing(false);
            }, 500);
          }
        }, 10);  // Update every 10ms, total time: about 0.2 second + 0.5 second pause
      } catch (error) {
        setParseError(error.message);
        setSuccess(false);
        setParsing(false);
      }
    } else {
      JSONParser.fromFile(file)
      .then(parser => {
        const data = parser.parse();
        const { competition, sessions, gymnasts, teams } = data;

        // Check if competition already exists
        if (parser.competitionExists(existingCompetitions)) {
          throw new Error("This competition already exists in the loaded data");
        }

        // Error checking
        if (!competition || Object.keys(competition).length === 0) {
          throw new Error("Failed to parse competition information");
        }
        if (!sessions || sessions.length === 0) {
          console.warn("No sessions found in the parsed data");
        }
        if (!gymnasts || gymnasts.length === 0) {
          console.warn("No gymnasts found in the parsed data");
        }

        if (!teams || teams.length === 0) {
          console.warn("No teams found in the parsed data");
        }

        // Additional error checking for new fields
        if (!data.coaches || data.coaches.length === 0) {
          console.warn("No coaches found in the parsed data");
        }
        if (!data.ageGroups || data.ageGroups.length === 0) {
          console.warn("No age groups found in the parsed data");
        }
        if (!data.levels || data.levels.length === 0) {
          console.warn("No levels found in the parsed data");
        }

        // console.log('Parse data:', data);

        // Simulate processing time with a pause at 100%
        let progress = 0;
        const interval = setInterval(() => {
          progress += 5;
          setParseProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            // Pause for 1 second at 100% before setting results
            setTimeout(() => {
              setParseResults(data);
              setSuccess(true);
              setParsing(false);
            }, 500);
          }
        }, 10);  // Update every 10ms, total time: about 0.2 second + 0.5 second pause
      })
      .catch((error) => {
        setParseError(error.message);
        setSuccess(false);
        setParsing(false);
      });
    }
  }

  const processFileCSV = async (file, type) => {
    setParsing(true);
    setParseProgress(0);
    setParseError(null);

    try {
      const data = await processCSVFile(file, type);
      // console.log(data)

      // Simulate processing time
      let progress = 0;
      const interval = setInterval(() => {
        progress += 5;
        setParseProgress(progress);
        if (progress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setParseResults(data);
            setSuccess(true);
            setParsing(false);
          }, 500);
        }
      }, 10);
    } catch (error) {
      setParseError(error.message);
      setSuccess(false);
      setParsing(false);
    }
  };

  const processFilePTF = (file) => {
    setParsing(true);
    setParseProgress(0);
    setParseError(null);
    PTFParser.fromFile(file)
      .then(parser => {
        const sections = parser.parse();
        // Get the data
        const competition = parser.getCompetitionInfo();
        const sessions = parser.getSessions();
        const gymnasts = parser.getGymnasts();

        // Error checking
        if (!competition || Object.keys(competition).length === 0) {
          throw new Error("Failed to parse competition information");
        }
        if (!sessions || sessions.length === 0) {
          throw new Error("Failed to parse session information");
        }
        if (!gymnasts || gymnasts.length === 0) {
          throw new Error("Failed to parse gymnast information");
        }

        // Check if competition already exists
        if (parser.competitionExists(existingCompetitions)) {
          throw new Error("This competition already exists in the loaded data");
        }

        const data = {
          competition,
          sessions,
          gymnasts,
          levels: parser.getLevels(),
          teams: parser.getGyms(),
          judges: parser.getJudges(),
          coaches: parser.getCoaches(),
          ageGroups: parser.getAgeGroups(),
          ageGroupRuleset: parser.getAgeGroupsAsRuleset(), // Converted for generator
          events: parser.getEvents(),
          statistics: parser.getStatistics(),
          rotations: parser.getRotations(),
        };

        // Additional error checking for new fields
        if (!data.coaches || data.coaches.length === 0) {
          console.warn("No coaches found in the parsed data");
        }
        if (!data.ageGroups || data.ageGroups.length === 0) {
          console.warn("No age groups found in the parsed data");
        }
        if (!data.levels || data.levels.length === 0) {
          console.warn("No levels found in the parsed data");
        }

        // console.log('Parse data:', data);

        // Simulate processing time with a pause at 100%
        let progress = 0;
        const interval = setInterval(() => {
          progress += 5;
          setParseProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            // Pause for 1 second at 100% before setting results
            setTimeout(() => {
              setParseResults(data);
              setSuccess(true);
              setParsing(false);
            }, 500);
          }
        }, 10);  // Update every 10ms, total time: about 0.2 second + 0.5 second pause
      })
      .catch((error) => {
        setParseError(error.message);
        setSuccess(false);
        setParsing(false);
      });
  };

  // console.log('FileType:', FileType);
  // console.log('selectedFile:', selectedFile);
  // console.log('FileType mapping:', selectedFile ? resolveFileType(selectedFile.type) : null);

  return (
    <Box sx={{ width: '100%', position: 'relative' }} data-ui="file-upload-container">
      <input
        data-ui="file-upload-input"
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        accept={acceptedFormats || ".csv,.xlsx,.xls"}
      />
      {(error || parseError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || parseError}
        </Alert>
      )}
      {success && selectedFile && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ mb: 1 }}
          >
            Valid {FileType[validation?.type] || ''} file detected
          </Alert>
          <Paper sx={{
            p: 2, backgroundColor: theme =>
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
          }}>
            <Typography variant="subtitle2" gutterBottom>File Details:</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(auto, 30%) 1fr', gap: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Name:</Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>{selectedFile.name}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Size:</Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>{selectedFile.size}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Type:</Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>{resolveFileType(selectedFile.type)}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Modified:</Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>{selectedFile.lastModified}</Typography>
            </Box>
            {parseResults && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  {parseResults.type?.startsWith('CSV.') || parseResults.type?.startsWith('JSON.') ? 'Import Details:' : 'Competition Details:'}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(auto, 30%) 1fr', gap: 1 }}>
                  {parseResults.type?.startsWith('CSV.') || parseResults.type?.startsWith('JSON.') ? (
                    // CSV-specific statistics display
                    <>
                      {(parseResults.type === FileType.CSV.USAG_SANCTION_ATHLETES ||
                        parseResults.type === FileType.JSON.USAG_SANCTION_ATHLETES) && (
                          <>
                            {parseResults.type === FileType.CSV.USAG_SANCTION_ATHLETES &&
                              <>
                                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Sanction #:</Typography>
                                <Typography variant="body2" sx={{ pl: 2 }}>{`${parseResults?.competition?.sanctionOrg} #${parseResults?.competition?.sanctionId}` || 'N/A'}</Typography>
                              </>
                            }
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Athletes:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>
                              {parseResults.statistics.totalScratches > 0 
                                ? `${parseResults.statistics.totalGymnasts} - ${parseResults.statistics.totalScratches} (Scratches) = ${parseResults.statistics.totalGymnasts - parseResults.statistics.totalScratches}`
                                : parseResults.statistics.totalGymnasts}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Levels:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalLevels}</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Age Groups:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalAgeGroups}</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Clubs/Teams:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalClubs}</Typography>
                          </>
                        )}
                      {(parseResults.type === FileType.JSON.USAG_SANCTION_COACHES) && (
                        <>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Clubs:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalClubs}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Coaches:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalCoaches}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Active Coaches:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.activeCoaches}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Scratched Coaches:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.scratchedCoaches}</Typography>
                        </>
                      )}
                      {(parseResults.type === FileType.JSON.USAG_SANCTION_JUDGES) && (
                        <>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Judges:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalJudges}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Active Judges:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.activeJudges}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Scratched Judges:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.scratchedJudges}</Typography>
                        </>
                      )}
                      {(parseResults.type === FileType.JSON.USAG_SANCTION_INDIVIDUALS) && (
                        <>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Individuals:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>
                            {parseResults.statistics?.totalIndividuals || 0}
                            {parseResults.statistics?.totalIndividuals === 0 && " (No individuals registered yet)"}
                          </Typography>
                          {parseResults.statistics?.totalIndividuals > 0 && (
                            <>
                              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Athletes:</Typography>
                              <Typography variant="body2" sx={{ pl: 2 }}>
                                {parseResults.statistics.totalScratches > 0 
                                  ? `${parseResults.statistics.totalGymnasts} - ${parseResults.statistics.totalScratches} (Scratches) = ${parseResults.statistics.totalGymnasts - parseResults.statistics.totalScratches}`
                                  : parseResults.statistics.totalGymnasts}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Coaches:</Typography>
                              <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalCoaches}</Typography>
                              {parseResults.statistics.totalJudges > 0 && (
                                <>
                                  <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Judges:</Typography>
                                  <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalJudges}</Typography>
                                </>
                              )}
                              {parseResults.statistics.totalOther > 0 && (
                                <>
                                  <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Other Staff:</Typography>
                                  <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalOther}</Typography>
                                </>
                              )}
                              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Levels:</Typography>
                              <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalLevels}</Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Age Groups:</Typography>
                              <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalAgeGroups}</Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Clubs:</Typography>
                              <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalClubs}</Typography>
                            </>
                          )}
                        </>
                      )}
                      {(parseResults.type === FileType.CSV.USAG_SANCTION_CLUBS ||
                        parseResults.type === FileType.JSON.USAG_SANCTION_CLUBS) && (
                          <>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Clubs:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalClubs}</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Athletes:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalAthletes}</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Coaches:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalCoaches}</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total States:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalStates}</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>States:</Typography>
                            <Typography variant="body2" sx={{ pl: 2 }}>
                              {Object.entries(parseResults.statistics.stateBreakdown)
                                .map(([state, count]) => `${state} (${count})`)
                                .join(', ')}
                            </Typography>
                          </>
                        )}
                      {parseResults.type === FileType.CSV.COMPETITION && (
                        <>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Rows:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalRows}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Columns:</Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalColumns}</Typography>
                        </>
                      )}
                    </>
                  ) : (
                    // PTF/JSON competition details display
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Meet Name:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.competition.name}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Meet Date{parseResults.competition.startDate !== parseResults.competition.endDate ? 's' : ''}:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>
                        {parseResults.competition.startDate === parseResults.competition.endDate
                          ? parseResults.competition.startDate
                          : `${parseResults.competition.startDate} - ${parseResults.competition.endDate}`}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Facility:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.competition.facility}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Director:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.competition.director}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Levels:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>
                        {parseResults.levels.map(level => level.id).join(', ')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Sessions:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalSessions}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Gymnasts:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalGymnasts}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>With Scores:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>
                        {parseResults.statistics.totalWithScores || 0} event scores, {parseResults.statistics.totalWithAA || 0} all-around
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Teams:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalTeams}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Judges:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalJudges}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Events:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalEvents}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Coaches:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalCoaches}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap', pl: 2 }}>Total Age Groups:</Typography>
                      <Typography variant="body2" sx={{ pl: 2 }}>{parseResults.statistics.totalAgeGroups}</Typography>
                    </>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Box>
      )}
      {!success && !showMergePrompt && (
        <Paper
          data-ui="file-upload-dropzone"
          variant="outlined"
          onClick={() => fileInputRef.current.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: theme => `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 2,
            backgroundColor: theme => isDragging ?
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
              : 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            '&:hover': {
              backgroundColor: theme =>
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            },
          }}
        >
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: theme => isDragging ? theme.palette.primary.main : theme.palette.text.secondary,
              transition: 'color 0.2s ease',
            }}
          />
          <Typography variant="h6" color="textSecondary" align="center">
            {isDragging ? 'Drop your file here' : 'Drag and drop your file here'}
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            or click to browse
          </Typography>
        </Paper>
      )}
      {success && (
        <>
          <Box sx={{ width: '100%', height: 50, position: 'relative', mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={parsing ? parseProgress : 100}
              sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}
            />
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              sx={{ position: 'absolute', top: 20, left: 0, right: 0 }}
            >
              {parsing
                ? `Processing file... ${Math.round(parseProgress)}%`
                : (parseResults
                  ? `${selectedFile?.type?.startsWith('CSV.')
                    ? 'CSV'
                    : selectedFile?.type === FileType.PTF
                      ? 'PTF'
                      : selectedFile?.type?.startsWith('JSON.')
                        ? 'JSON'
                        : ''} file was successfully parsed!`
                  : 'Processing complete!')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              data-ui="btn-file-upload-import"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                px: 8,
                py: 2,
                fontSize: '1.2rem',
                minWidth: '200px'
              }}
              onClick={() => {
                if (selectedFile?.type?.startsWith('CSV.')) {
                  processFileCSV(selectedFile.file, selectedFile?.type);
                } else if ([FileType.PTF, FileType.JSON.COMPETITION].includes(selectedFile?.type)) {
                  if (parseResults) {
                    // Import functionality for competition files
                    const importSuccess = onImport(parseResults);
                    if (importSuccess) {
                      setSnackbarMessage('Competition imported successfully!');
                      setSnackbarOpen(true);
                      setSelectedFile(null);
                      setSuccess(false);
                      setValidation(null);
                      setError(null);
                      resetParseStates();
                      onClose();
                    }
                    return true;
                  } else {
                    if (selectedFile?.type === FileType.PTF) {
                      processFilePTF(selectedFile.file);
                    } else if (selectedFile?.type === FileType.JSON.COMPETITION) {
                      processFileJSON(selectedFile.file, selectedFile?.type);
                    }
                  }
                } else if (selectedFile?.type?.startsWith('JSON.')) {
                  // Handle other JSON file types
                  if (parseResults) {
                    let importSuccess = false;
                    switch (selectedFile.type) {
                      case FileType.JSON.USAG_SANCTION_ATHLETES:
                      case FileType.JSON.USAG_SANCTION_COACHES:
                      case FileType.JSON.USAG_SANCTION_JUDGES:
                      case FileType.JSON.USAG_SANCTION_CLUBS:
                      case FileType.JSON.USAG_SANCTION_INDIVIDUALS:
                        // Pass USAG data directly - it already has the correct type field
                        importSuccess = onImport(parseResults);
                        break;
                      default:
                        console.warn('Unhandled JSON file type:', selectedFile.type);
                        break;
                    }
                    
                    if (importSuccess) {
                      setSnackbarMessage(`${selectedFile.type.split('.').pop().toLowerCase()} data imported successfully!`);
                      setSnackbarOpen(true);
                      setSelectedFile(null);
                      setSuccess(false);
                      setValidation(null);
                      setError(null);
                      resetParseStates();
                      onClose();
                    }
                  } else {
                    processFileJSON(selectedFile.file, selectedFile?.type);
                  }
                }
              }}
              disabled={parsing || (!parseResults && selectedFile?.type === FileType.PTF)}
            >
              {parsing ? 'Processing...' : (parseResults ? 'Import' : 'Process')}
            </Button>
            <Button
              data-ui="btn-file-upload-reset"
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                minWidth: '100px'
              }}
              onClick={() => {
                setSelectedFile(null);
                setSuccess(false);
                setValidation(null);
                setError(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            >
              Reset
            </Button>
          </Box>
        </>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
});

FileUpload.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  onImport: PropTypes.func,
  acceptedFormats: PropTypes.string,
  existingCompetitions: PropTypes.array,
  onClose: PropTypes.func,
  onDuplicateFound: PropTypes.func,
  showMergePrompt: PropTypes.bool
};

export default FileUpload;
